<script setup lang="ts">
interface Toggle {
	active: boolean,
	value: boolean
}

const props = defineProps({
	icon: {
		type: String,
		default: ""
	},
	outline: {
		type: Boolean,
		default: false
	},
	fill: {
		type: Boolean,
		default: false
	},
	wide: {
		type: Boolean,
		default: false
	},
	tag: {
		type: String,
		default: "button"
	},
	/** Toggle mode only works with outline mode */
	toggle: {
		type: Object as PropType<Toggle>,
		default () {
			return {
				active: false,
				value: false
			};
		}
	},
	color: {
		type: String,
		default: "violet",
		validator (v: string) {
			return ["violet", "rose", "emerald"].includes(v);
		}
	}
});

const colorSchemas: Record<string, any> = {
	violet: {
		base: `text-gray-900 hover:bg-gray-50 active:text-violet-600 ${props.toggle.active ? "" : "focus:elevation-cta-shadow"}`,
		fill: "enabled:bg-violet-600 enabled:text-gray-0 enabled:hover:bg-violet-500 enabled:active:bg-violet-700 enabled:focus:elevation-cta-shadow",
		outline: `border border-gray-200 ${props.toggle.active ? "" : "focus:elevation-cta-shadow-border"}`,
		toggle: "elevation-cta-shadow-border"
	},
	rose: {
		base: `text-rose-500 hover:bg-rose-50 active:text-rose-500 ${props.toggle.active ? "" : "focus:elevation-cta-shadow-border-red"}`,
		fill: "enabled:bg-rose-500 enabled:text-gray-0 enabled:hover:bg-rose-400 enabled:active:bg-rose-600 enabled:focus:elevation-cta-shadow-red",
		outline: `border border-rose-200 ${props.toggle.active ? "" : "focus:elevation-cta-shadow-border-red"}`,
		toggle: "elevation-cta-shadow-border-red"
	},
	emerald: {
		base: `text-gray-900 hover:bg-gray-50 active:text-emerald-500 ${props.toggle.active ? "" : "focus:elevation-cta-shadow-border-green"}`,
		fill: "enabled:bg-emerald-500 enabled:text-gray-0 enabled:hover:bg-emerald-400 enabled:active:bg-emerald-600 enabled:focus:elevation-cta-shadow-red",
		outline: `border border-emerald-200 ${props.toggle.active ? "" : "focus:elevation-cta-shadow-border-green"}`,
		toggle: "elevation-cta-shadow-border-green"
	}
};

const currentColorSchema = colorSchemas[props.color];
const fillClasses = computed(() => props.fill
	? currentColorSchema.fill
	: currentColorSchema.base);
const outlineClasses = computed(() => props.outline && currentColorSchema.outline);
const wideClasses = computed(() => props.wide && "w-[128px] justify-center");
const toggleClasses = computed(() => props.toggle.active ? props.toggle.value && currentColorSchema.toggle : "");
const buttonClasses = computed(() => [fillClasses.value, outlineClasses.value, wideClasses.value, toggleClasses.value]);
</script>

<template>
	<component
		:is="tag"
		class="flex items-center justify-start gap-x-2 h-6 px-2 rounded-sm cursor-pointer disabled:bg-gray-200 disabled:text-gray-400 duration-75"
		:class="buttonClasses"
	>
		<NuxtIcon
			v-if="icon"
			:name="icon"
			class="text-[20px]"
		/>
		<span class="font-semibold text-sm whitespace-nowrap select-none">
			<slot></slot>
		</span>
	</component>
</template>
